import dominantEnergyMeanings from '../data/dominantEnergyMeanings.js';
import movingLineMeanings from '../data/movingLineMeanings.js';
import { getPhilosophyContent } from '../data/thamPhaiHieu.js';

const getMovingLineMeaning = (hexagramName, lineNumber) => {
  if (!movingLineMeanings[hexagramName] || !movingLineMeanings[hexagramName][lineNumber]) {
    return 'Thông tin chuyên sâu về hào động đang được cập nhật';
  }
  return movingLineMeanings[hexagramName][lineNumber];
};

const formatPhilosophyContent = (philosophy) => {
  if (!philosophy) return '';
  
  const { mainPillars } = philosophy;
  
  const formatKeyPoints = (points) => {
    return points.map(point => {
      let content = `#### ${point.title}\n`;
      
      if (point.aspects) {
        Object.entries(point.aspects).forEach(([key, value]) => {
          if (value.personalityTraits) {
            content += `- **Khía cạnh cá nhân:**\n${value.personalityTraits.map(trait => `  • ${trait}`).join('\n')}\n`;
          }
          if (value.coreValues) {
            content += `- **Giá trị cốt lõi:**\n${value.coreValues.map(val => `  • ${val}`).join('\n')}\n`;
          }
        });
      }
      
      if (point.methods) {
        if (Array.isArray(point.methods)) {
          content += `- **Phương pháp:**\n${point.methods.map(method => `  • ${method}`).join('\n')}\n`;
        } else if (point.methods.approaches) {
          content += `- **Cách tiếp cận:**\n${point.methods.approaches.map(approach => `  • ${approach}`).join('\n')}\n`;
        }
      }
      
      if (point.humanNature) {
        content += `- **Bản chất con người:**\n`;
        content += `  • ${point.humanNature.emotions}\n`;
        content += `  • ${point.humanNature.inherentNature}\n`;
      }
      
      return content;
    }).join('\n');
  };

  let content = `### ${philosophy.title}\n\n`;
  
  // Format Tham Phải Hiểu section
  content += `### ${mainPillars.thamPhaiHieu.title}\n`;
  content += formatKeyPoints(mainPillars.thamPhaiHieu.keyPoints);
  
  // Format Chọn Đúng Người section
  content += `\n### ${mainPillars.chonDungNguoi.title}\n`;
  mainPillars.chonDungNguoi.keyChoices.forEach(choice => {
    content += `#### ${choice.title}\n`;
    content += `- ${choice.description}\n`;
    if (choice.importance) content += `- Tầm quan trọng: ${choice.importance}\n`;
    if (choice.details) {
      content += `- Chi tiết:\n`;
      Object.entries(choice.details).forEach(([key, value]) => {
        if (typeof value === 'string') {
          content += `  • ${value}\n`;
        }
      });
    }
  });

  // Format Sống Như Ý section
  content += `\n### ${mainPillars.songNhuY.title}\n`;
  content += `Nguyên tắc: ${mainPillars.songNhuY.principles.join(', ')}\n`;

  // Format Giới-Định-Tuệ section
  content += `\n### ${mainPillars.gioiDinhTue.title}\n`;
  Object.entries(mainPillars.gioiDinhTue.elements).forEach(([key, element]) => {
    content += `#### ${element.title}\n`;
    content += `- ${element.description}\n`;
    if (element.principles) {
      content += `- Nguyên tắc:\n${element.principles.map(p => `  • ${p}`).join('\n')}\n`;
    }
    if (element.benefits) {
      content += `- Lợi ích: ${element.benefits}\n`;
    }
    if (element.relationship) {
      content += `- Mối quan hệ:\n${element.relationship.map(r => `  • ${r}`).join('\n')}\n`;
    }
  });

  return content;
};

export const generatePrompt = (hexagramData, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice) => {
  const dominantEnergyMeaning = dominantEnergy
    ? `Năng lượng chủ đạo **${dominantEnergy}** chi phối toàn bộ dãy số: 
       """${dominantEnergyMeanings[dominantEnergy]?.description || 'Đang hoàn thiện dữ liệu chuyên sâu'}"""`
    : 'Không xác định được năng lượng chủ đạo';

  const formatNotifications = (notifications) => {
    if (!notifications?.length) return '';
    return notifications.map(item => 
      `▸ **${item.code}** (${item.description}): ${item.meaning?.replace(/\n\s*-/g, ' ').replace(/\n+/g, ' ') || 'Phân tích chi tiết đang được cập nhật'}`
    ).join('\n\n');
  };

  const formatGhiChu = (warnings) => {
    if (!warnings?.length) return '';
    return warnings.map(item => 
      item.title.includes('nhiều 0') 
        ? `**${item.title}**: ${item.description} - Cảnh báo đặc biệt cần xem xét kỹ lưỡng`
        : `**${item.title}**: ${item.description}`
    ).join('. ');
  };

  const formatCombinedPairs = (pairs) => {
    if (!pairs?.length) return '';
    return pairs.map(pair => {
      const meaning = pair.meaning?.split('\n').map(line => 
        line.trim().replace(/^-/, '').replace(/:/, ': ')
      ).join(' ') || 'Ý nghĩa chuyên sâu đang được hoàn thiện';
      return `**${pair.number}** (${pair.energy}): ${meaning}`;
    }).join('\n\n');
  };

  const formatNumberPairs = (pairs) => {
    if (!pairs?.length) return 'Không phát hiện tổ hợp đặc biệt';
    return pairs.map(pair => 
      `▸ **${pair.pair}** (quẻ ${pair.hexagram}): ${pair.hexagramMeaning}`
    ).join('\n');
  };

  const formatEnergyDetails = (pairs) => {
    if (!pairs?.length) return 'Không xác định được dòng chảy năng lượng';
    
    const items = pairs.map(p => `${p.number} (${p.energy})`);
    if (items.length === 1) return `Bắt đầu và kết thúc với **${items[0]}**`;
    
    return items.slice(0, -1).join(', ') + ` và cuối cùng là **${items[items.length-1]}**`;
  };

  let additionalNotices = [];
  if (notificationList?.length) {
    additionalNotices.push(`**Tổ hợp cảnh báo**:\n${formatNotifications(notificationList)}`);
  }
  if (ghiChuList?.length) {
    additionalNotices.push(`**Ghi chú đặc biệt**: ${formatGhiChu(ghiChuList)}`);
  }
  if (warningNotice) {
    additionalNotices.push(`**Cảnh báo số đuôi**: ${warningNotice.title} - ${warningNotice.subtitle}\n${warningNotice.points.map(p => `▸ ${p}`).join('\n')}`);
  }

  const movingLineMeaning = getMovingLineMeaning(hexagramData.main.name, hexagramData.movingLine);

  const philosophyContent = getPhilosophyContent();
  const formattedPhilosophy = formatPhilosophyContent(philosophyContent);
  const philosophySection = formattedPhilosophy ? `
### Triết lý "Tham Phải Hiểu" áp dụng cho trường hợp này

${formattedPhilosophy}` : '';

  return `##RESPONSIBILITY## 
You will act as an extremely strict but constructive sales coach, thoroughly testing and assessing my mastery of the NEPQ questioning framework through a role-play sales interaction. I am your sales trainee. Write your response in Vietnamese.

<input>
${businessContext ? `- **Bối cảnh kinh doanh**: ${businessContext}` : ''}
${interactionGoal ? `- **Mục tiêu giao dịch**: ${interactionGoal}` : ''}

Thông tin khách hàng:
- **SĐT**: ${hexagramData.phoneNumber}
- **Nhân khẩu học**: ${gender}, ${ageRange}, ${maritalStatus}
- **Công việc chính**: ${job || 'Chưa xác định'}

Thông tin quẻ Kinh Dịch:
- **Quẻ chính**: ${hexagramData.main.name} 
  → *${hexagramData.main.meaning}*
- **Hào động ${hexagramData.movingLine}**: 
  → *${movingLineMeaning}*
- **Quẻ biến**: ${hexagramData.transformed.name} 
  → *${hexagramData.transformed.meaning}*
- **Các quẻ Dịch then chốt**:
${formatNumberPairs(hexagramData.numberPairs)}
Thông tin năng lượng số:
- **Chuỗi tổ hợp năng lượng số**:
${formatEnergyDetails(combinedPairs)}
- **Năng lượng chủ đạo**:
${dominantEnergyMeaning}
- **Phân tích từng tổ hợp năng lượng số**:
${formatCombinedPairs(combinedPairs)}
- **Cảnh báo đặc biệt**:
${additionalNotices.join('\n\n') || 'Không có cảnh báo đặc biệt'}
</input>

##PROCESS##
After I say "Bắt đầu", you will role-play a typical prospect (use context from <input>) with objections, engaging in a realistic sales conversation with me as the salesperson for about 15-20 minutes (requiring a minimum of 15 interactions from me using NEPQ questions). 

Only when I say "Kết thúc", you will end the role-play and provide an assessment. After the session, you will comprehensively and strictly evaluate my questioning skills based on the NEPQ framework, constructively highlighting both strengths and areas for improvement.

You will then assign a grade out of 100, with a maximum score if I skillfully demonstrated over 8 NEPQ questioning techniques. Be strict - if I used fewer than 3 NEPQ techniques, I fail (score below 50). Also fail me if there are less than 15 turns of me asking questions. The assessment must be objective and aligned with the NEPQ framework.

I automatically fail if I make 2 or more of these errors: 
1) Being pushy, aggressive or using high-pressure tactics
2) Making assumptions about the prospect without asking questions 
3) Telling my story instead of focusing on the prospect's situation
4) Presenting features/benefits without understanding prospect's problems
5) Failing to uncover the prospect's core problems and desired results

##DETAILS##
The NEPQ questioning skills to assess include:
1) Connection questions to disarm prospects 
2) Situation questions to understand current state
3) Problem Awareness questions to identify core issues
4) Solution Awareness questions to envision desired future state
5) Consequence questions to build urgency for change 
6) Transition questions to segue into presenting the solution
7) Checking for Agreement to keep prospect engaged when presenting
8) Qualifying questions to gauge commitment to change
9) Commitment questions to advance the sale
10) Probing/clarifying questions to go deeper into prospect's answers

##REQUIREMENTS## 
Always act as an authentic prospect during the role-play, starting when I say "Bắt đầu". When I say "Kết thúc", provide the assessment results professionally and bluntly in Vietnamese. Don't hesitate to fail me if I don't demonstrate the NEPQ skills sufficiently.

##RESTRICTIONS##
NEVER TAKE THE ROLE OF SALESPERSON, ONLY BE THE PROSPECT, THEN THE STRICT ASSESSOR. 
AS THE PROSPECT, DON'T USE NEPQ KNOWLEDGE, JUST BE A TYPICAL PROSPECT WITH CONCERNS - LET ME USE NEPQ TO PERSUADE YOU.
`;
};